import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../redux';
import { updateSortConfiguration } from '../../../../redux/ChartPoperties/ChartControlsActions';
import RenderList from './RenderList';
import { limitChartData } from '../../../CommonFunctions/CommonFunctions';

interface Props {
  propKey: string;
  sortConfigIndex: number;
  color?: string;
}

export interface ListItem {
  id: string;
  text: string;
  value: any;
}

/**
 *
 * @returns Manual sort logic showing list for selected column.
 * For calendar chart, we are sorting by year of date.
 */
const ManualSort = ({ propKey, sortConfigIndex, color }: Props) => {
  const [items, setItems] = useState<ListItem[]>([]);

  const { chartType, chartData, chartAxes, sortConfiguration } = useSelector(
    (state: RootState) => ({
      chartType: state.chartProperties.properties[propKey].chartType,
      chartData: state.chartControls.properties[propKey].chartData || [],
      chartAxes: state.chartProperties.properties[propKey]?.chartAxes,
      sortConfiguration:
        state.chartControls.properties[propKey].sortConfigurations?.[sortConfigIndex],
    }),
  );

  const dispatch = useDispatch();

  const fieldName = sortConfiguration?.fieldName || '';

  // Apply the same limitChartData logic that charts use
  const limitedChartData = useMemo(() => {
    if (chartData.length === 0) return chartData;

    // Get the max limit based on chart type
    let maxLimit: number;
    switch (chartType) {
      case 'pie':
      case 'donut':
      case 'rose':
        maxLimit = 50;
        break;
      case 'horizontalBar':
      case 'multibar':
      case 'stackedBar':
      case 'horizontalStacked':
      case 'waterfall':
        maxLimit = 100;
        break;
      case 'line':
      case 'area':
      case 'stackedArea':
        maxLimit = 200;
        break;
      case 'boxPlot':
      case 'scatterPlot':
      case 'treeMap':
      case 'sankey':
      case 'heatmap':
        maxLimit = 5000;
        break;
      default:
        maxLimit = 50; // Default limit
    }

    // Get the display name for limitChartData based on chart structure
    // Different chart types have different axis structures
    // Pie, Donut, Rose use chartAxes[2]
    // Most bar/line charts use chartAxes[3]
    let displayName: string | undefined;
    if (
      chartType === 'pie' ||
      chartType === 'donut' ||
      chartType === 'rose' ||
      chartType === 'waterfall' ||
      chartType === 'sankey'
    ) {
      displayName = chartAxes?.[2]?.fields?.[0]?.displayname;
    } else {
      displayName = chartAxes?.[3]?.fields?.[0]?.displayname;
    }

    if (!displayName) return chartData;

    return limitChartData(chartData, maxLimit, displayName);
  }, [chartData, chartType, chartAxes]);

  //------ Extract unique values from limitedChartData for the selected field ------//
  const uniqueValues = useMemo(() => {
    if (!fieldName || limitedChartData.length === 0) return [];

    const valuesSet = new Set<any>();
    limitedChartData.forEach((row: any) => {
      const value = row[fieldName];
      if (value !== undefined && value !== null) {
        if (chartType === 'calendar') {
          const dateValue = new Date(value);
          if (!isNaN(dateValue.getTime())) {
            valuesSet.add(dateValue.getFullYear());
          }
        } else {
          valuesSet.add(value);
        }
      }
    });
    let arrayFromValueSet = Array.from(valuesSet);
    if (!sortConfiguration.manualSortOrderObject) {
      arrayFromValueSet.sort((a, b) => a - b);
    }
    return arrayFromValueSet;
  }, [limitedChartData, fieldName, chartType, sortConfiguration?.manualSortOrderObject]);

  useEffect(() => {
    if (uniqueValues.length > 0) {
      let orderedItems: ListItem[];

      // If manual sort order exists, sort items according to the saved manual order
      if (sortConfiguration?.manualSortOrderObject) {
        const sortOrder = sortConfiguration.manualSortOrderObject;

        const itemsMap = uniqueValues.map((value, index) => ({
          id: `${value}_${index}`,
          text: String(value),
          value: value,
        }));

        orderedItems = itemsMap.sort((a, b) => {
          const orderA = sortOrder[String(a.value)] ?? 999999;
          const orderB = sortOrder[String(b.value)] ?? 999999;
          return orderA - orderB;
        });
      } else {
        // No manual sort order yet
        orderedItems = uniqueValues.map((value, index) => ({
          id: `${value}_${index}`,
          text: String(value),
          value: value,
        }));
      }

      setItems(orderedItems);
    }
    // eslint-disable-next-line
  }, [uniqueValues, sortConfiguration?.manualSortOrderObject]);

  const moveItem = (from: number, to: number) => {
    setItems((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      updateManualSortOrder(updated);
      return updated;
    });
  };

  //----- Update the manual sort order in the configuration ----//
  const updateManualSortOrder = (orderedItems: ListItem[]) => {
    if (!fieldName || !sortConfiguration) return;

    const manualSortOrderObject: Record<string, number> = {};
    orderedItems.forEach((item, index) => {
      manualSortOrderObject[String(item.value)] = index;
    });

    const updatedConfig = {
      ...sortConfiguration,
      manualSortOrderObject,
    };

    dispatch(updateSortConfiguration(propKey, sortConfigIndex, updatedConfig));
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        marginTop: '0.5rem',
        paddingInline: '2px',
      }}
    >
      {items.length > 0 ? (
        <>
          <div
            style={{
              fontSize: '11px',
              color: color || '#666',
              fontStyle: 'italic',
              marginBottom: '4px',
              textAlign: 'left',
            }}
          >
            Drag to reorder
          </div>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              marginBottom: '4px',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            {items.map((item, index) => (
              <RenderList
                key={item.id}
                item={item}
                index={index}
                moveItem={moveItem}
                color={color}
              />
            ))}
          </ul>
        </>
      ) : (
        <div
          style={{
            fontSize: '12px',
            color: color || '#999',
            padding: '16px',
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          No data available for manual sorting
        </div>
      )}
    </div>
  );
};

export default ManualSort;
